import { Injectable, Inject, InjectionToken, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subscriber } from 'rxjs';
import { delay, map, timeout } from 'rxjs/operators';
import { MetricQuery, MetricResult, MetricScope, MetricKey, MetricEvidence, CompanyAccess, DashboardConfig, CompanyWidgetKey, DashboardLayout, LayoutScope } from '../interfaces/metrics';

// Default forecast horizon; mirrors the backend MetricsService::DEFAULT_HORIZON_WEEKS.
export const DEFAULT_HORIZON_WEEKS = 8;

// false (default) = deterministic mock; true = real /api/metrics endpoint (backend P1).
export const USE_METRICS_API = new InjectionToken<boolean>('USE_METRICS_API');

const FINANCIAL_KEYS: MetricKey[] = ['contribution.self', 'company.margin', 'company.margin_by_client', 'team.margin'];

// Mirrors the backend's notApplicable resolvers (schedule/queue/roster facts that count
// no hours at all) - see the per-resolver evidence comments in app/Services/Metrics/Resolvers.
const NOT_APPLICABLE_KEYS: MetricKey[] = [
  'timesheet.status', 'assignments.ending', 'leave.self',
  'team.to_approve', 'team.capacity_forecast', 'team.rolloff',
  'company.attention', 'company.bench', 'company.bench_forecast', 'company.bench_roster', 'company.coming_free',
];

// One subscribed get() awaiting the next batch flush.
interface PendingQuery { sig: string; query: MetricQuery; subscriber: Subscriber<MetricResult>; }

@Injectable({ providedIn: 'root' })
export class MetricsService {
  // The dashboard mounts ~9 widgets that each call get() synchronously in the
  // same change-detection tick. Instead of firing 9 POSTs (9x Laravel
  // bootstrap+auth, plus literal duplicate queries e.g. assignments.ending
  // rendered as both a KPI and a list), we buffer within the tick and flush one
  // /metrics/query-batch. Identical queries collapse to a single backend compute
  // and fan back out to every caller.
  private buffer: PendingQuery[] = [];
  private flushScheduled = false;

  constructor(@Optional() private http: HttpClient,
              @Optional() @Inject(USE_METRICS_API) private useApi: boolean) {}

  get<K extends MetricKey>(q: MetricQuery & { key: K }): Observable<MetricResult<K>> {
    if (this.useApi && this.http) {
      // Cold, like the HttpClient it replaces: enqueue on SUBSCRIBE, not on
      // call. Every widget subscribes in the same change-detection tick, so
      // their queries buffer together and flush as one /metrics/query-batch. A
      // caller that defers its subscription still forms (or joins) a batch at
      // subscribe time instead of missing a flush that already ran.
      return new Observable<MetricResult>(subscriber => {
        const entry: PendingQuery = { sig: this.sig(q), query: q, subscriber };
        this.buffer.push(entry);
        this.scheduleFlush();
        // Unsubscribed before the flush fires -> drop it from the batch so a
        // canceled widget isn't a wasted query. (After flush, the entry is in
        // the local pending snapshot and this is a harmless no-op.)
        return () => {
          const i = this.buffer.indexOf(entry);
          if (i !== -1) { this.buffer.splice(i, 1); }
        };
      }) as Observable<MetricResult<K>>;
    }
    if (this.useApi !== false) {
      console.warn('MetricsService: mock data in use — USE_METRICS_API/http not configured');
    }
    return of(this.mock(q) as MetricResult<K>).pipe(delay(350)); // visible skeleton in dev
  }

  private scheduleFlush(): void {
    if (this.flushScheduled) { return; }
    this.flushScheduled = true;
    // Microtask: fires after the current synchronous CD pass, so every widget's
    // subscribe in this tick lands in the same batch.
    Promise.resolve().then(() => this.flush());
  }

  private sig(q: MetricQuery): string {
    const s: MetricScope = q.scope;
    const scopeSig = s.type === 'self' || s.type === 'company' ? s.type : `${s.type}:${(s as any).id}`;
    return `${q.key}|${scopeSig}|${q.period.type}|${q.period.anchor}|${q.horizonWeeks ?? ''}`;
  }

  private flush(): void {
    const pending = this.buffer;
    this.buffer = [];
    this.flushScheduled = false;
    if (pending.length === 0) { return; }

    // Dedup by signature, preserving first-seen order for a stable response mapping.
    const uniqueSigs: string[] = [];
    const queryBySig = new Map<string, MetricQuery>();
    for (const p of pending) {
      if (!queryBySig.has(p.sig)) { queryBySig.set(p.sig, p.query); uniqueSigs.push(p.sig); }
    }
    const queries = uniqueSigs.map(sig => queryBySig.get(sig)!);

    this.http!.post<MetricResult[]>('/api/metrics/query-batch', { queries }, { withCredentials: true })
      .pipe(timeout(10000))
      .subscribe({
        next: results => {
          const resultBySig = new Map<string, MetricResult>();
          uniqueSigs.forEach((sig, i) => resultBySig.set(sig, results[i]));
          for (const p of pending) {
            const r = resultBySig.get(p.sig);
            // Guard each fan-out: a subscriber that throws synchronously in its
            // own handler must not strand the remaining pending widgets on their
            // loading skeletons. Each subscriber owns its own error. (next/error
            // are safe no-ops on an already-unsubscribed subscriber.)
            try {
              if (r) { p.subscriber.next(r); p.subscriber.complete(); }
              else { p.subscriber.error(new Error('metrics batch: missing slot for ' + p.sig)); }
            } catch { /* subscriber's pipe threw; it already owns that error */ }
          }
        },
        error: err => { for (const p of pending) { try { p.subscriber.error(err); } catch { /* owns its error */ } } },
      });
  }

  managedDepartments(): Observable<{ id: number; name: string }[]> {
    if (this.useApi && this.http) {
      return this.http.get<{ id: number; name: string }[]>('/api/metrics/team/departments', { withCredentials: true }).pipe(timeout(10000));
    }
    return of([{ id: 1, name: 'Delivery (demo)' }]);
  }

  companyAccess(): Observable<CompanyAccess> {
    if (this.useApi && this.http) {
      return this.http.get<CompanyAccess>('/api/metrics/company/access', { withCredentials: true }).pipe(timeout(10000));
    }
    return of({ canView: true, canConfigure: true, name: 'FLIX-IT (demo)', target: null, marginTarget: null, config: { hidden: [], horizonWeeks: DEFAULT_HORIZON_WEEKS }, viewBillRate: true });
  }

  // PATCH the per-tenant company dashboard config; the endpoint persists visibility
  // + target + marginTarget + horizon in one transaction and returns the saved values.
  // `revision` is the token the last read handed out; the endpoint 409s when the
  // stored config has moved since (#1107). Omitting it keeps the old
  // last-write-wins behaviour, which is what a tab loaded before this shipped does.
  updateDashboardConfig(body: { hidden: CompanyWidgetKey[]; target: number | null; marginTarget: number | null; horizonWeeks: number | null; revision?: string }): Observable<{ target: number | null; marginTarget: number | null; config: DashboardConfig; revision?: string }> {
    if (this.useApi && this.http) {
      return this.http.patch<{ target: number | null; marginTarget: number | null; config: DashboardConfig; revision?: string }>('/api/metrics/company/dashboard-config', body, { withCredentials: true }).pipe(timeout(10000));
    }
    return of({ target: body.target, marginTarget: body.marginTarget, config: { hidden: body.hidden, horizonWeeks: body.horizonWeeks ?? DEFAULT_HORIZON_WEEKS }, revision: body.revision });
  }

  // Per-user, per-tenant widget layout. The endpoint normalizes over the scope
  // catalog, so a real response always positions every visible widget; the mock
  // arms return {} and let the grid fill missing ids from its client-side default.
  // Returns the revision alongside the layout so a save can prove it is building
  // on what it read; a second tab holding an older one is refused (#1107).
  getDashboardLayout(scope: LayoutScope): Observable<{ layout: DashboardLayout; revision?: string }> {
    if (this.useApi && this.http) {
      return this.http.get<{ layout: DashboardLayout; revision?: string }>(`/api/metrics/dashboard-layout/${scope}`, { withCredentials: true })
        .pipe(timeout(10000));
    }
    return of({ layout: {} });
  }

  saveDashboardLayout(scope: LayoutScope, layout: DashboardLayout, revision?: string): Observable<{ layout: DashboardLayout; revision?: string }> {
    if (this.useApi && this.http) {
      return this.http.put<{ layout: DashboardLayout; revision?: string }>(`/api/metrics/dashboard-layout/${scope}`, { layout, revision }, { withCredentials: true })
        .pipe(timeout(10000));
    }
    return of({ layout, revision });
  }

  private base(q: MetricQuery): { key: MetricKey; stale: boolean; evidence: MetricEvidence } {
    if (NOT_APPLICABLE_KEYS.includes(q.key)) {
      return { key: q.key, stale: false, evidence: { notApplicable: true } };
    }
    const approvalStatesIncluded: MetricEvidence['approvalStatesIncluded'] = FINANCIAL_KEYS.includes(q.key)
      ? ['approved']
      : ['submitted', 'approved'];
    return {
      key: q.key, stale: false,
      evidence: {
        closed: true, approvalStatesIncluded,
        numeratorSource: 'mock', denominatorSource: 'mock',
        asOf: new Date().toISOString(),
      },
    };
  }

  private mock(q: MetricQuery): MetricResult<any> {
    const b = this.base(q);
    switch (q.key) {
      case 'hours.week': return { ...b, state: 'ok', data: { registered: 32.5, planned: 36, sparkline: [30, 34, 31, 36, 33, 35, 32.5] } };
      case 'hours.byday': {
        if (q.period.anchor.startsWith('2026-01')) return { ...b, state: 'empty' };
        return { ...b, state: 'ok', data: {
          days: [
            { day: 'mo', billable: 6.5, nonBillable: 1.5 }, { day: 'tu', billable: 7, nonBillable: 1 },
            { day: 'we', billable: 5.5, nonBillable: 2.5 }, { day: 'th', billable: 7.5, nonBillable: 0.5 },
            { day: 'fr', billable: 6, nonBillable: 0 }],
          totals: { registered: 32.5, billable: 28, nonBillable: 4.5, planned: 36 } } };
      }
      case 'hours.heatmap': {
        const cells = []; let seed = 7;
        for (let w = 0; w < 16; w++) for (let d = 0; d < 5; d++) {
          seed = (seed * 9301 + 49297) % 233280;
          cells.push({ weekIndex: w, dayIndex: d, hours: Math.round((seed / 233280) * 8 * 10) / 10 });
        }
        // weekStarts: 16 Mondays ending at the query's anchor week, oldest first (index 0
        // lines up with cells' weekIndex 0). The interface requires this field; the mock
        // dropping it threw in hours-heatmap's unguarded .map() whenever USE_METRICS_API
        // is flipped off for local debugging.
        const anchor = new Date(q.period.anchor + 'T12:00:00Z');
        anchor.setUTCDate(anchor.getUTCDate() - ((anchor.getUTCDay() + 6) % 7));
        const weekStarts = Array.from({ length: 16 }, (_, i) => {
          const monday = new Date(anchor);
          monday.setUTCDate(monday.getUTCDate() - (15 - i) * 7);
          return monday.toISOString().slice(0, 10);
        });
        return { ...b, state: 'ok', data: { weeks: 16, weekStarts, cells, maxHours: 8 } };
      }
      case 'utilization.self': return { ...b, state: 'ok', data: { value: 78, target: 80, deltaPrevPeriod: 3 } };
      case 'timesheet.status': return { ...b, state: 'ok', data: {
        current: { weekStart: '2026-06-29', status: 'open' }, previous: { weekStart: '2026-06-22', status: 'approved' } } };
      case 'assignments.ending': return { ...b, state: 'ok', data: { rows: [
        { client: 'Rabobank', role: 'Data engineer', endsOn: '2026-08-15', hoursThisMonth: 124, internal: false },
        { client: 'ASML', role: 'BI consultant', endsOn: '2026-09-30', hoursThisMonth: 18, internal: false },
        { client: 'FLIX internal', role: 'Kennisdeling', endsOn: null, hoursThisMonth: 4.5, internal: true }] } };
      case 'leave.self': return { ...b, state: 'ok', data: { takenDays: 12 } };
      case 'contribution.self': return { ...b, state: 'ok', data: { amount: 14880, deltaPrevPeriod: 6 } };
      case 'team.summary': return { ...b, state: 'ok', data: {
        memberCount: 4, teamUtilization: 82, benchCount: 1, hoursTotal: 138, approvalBacklogThisWeek: 2 } };
      case 'team.roster': return { ...b, state: 'ok', data: { rows: [
        { userId: 27, name: 'Ahmed Bakr', utilization: 95, hours: 38, timesheetStatus: 'submitted', assignmentEndsOn: '2026-08-15' },
        { userId: 31, name: 'Eva de Vries', utilization: 44, hours: 18, timesheetStatus: 'open', assignmentEndsOn: null },
        { userId: 42, name: 'Joost Meijer', utilization: 101, hours: 41, timesheetStatus: 'approved', assignmentEndsOn: '2026-09-30' } ] } };
      case 'team.margin': return { ...b, state: 'ok', data: {
        marginPct: 38, coveragePct: 96, coveredHrs: 512, billableHrs: 533, asOf: '2026-07-06' } };
      case 'team.capacity_forecast': return { ...b, state: 'ok', data: {
        horizonWeeks: 13, thresholdPct: 50, asOf: '2026-07-20',
        totals: { capacityHours: 1872, committedHours: 1544, committedPct: 82, freeFteWeeks: 8.2, atRiskMembers: 2 },
        weeks: Array.from({ length: 13 }, (_, i) => ({
          weekStart: ['2026-07-20', '2026-07-27', '2026-08-03', '2026-08-10', '2026-08-17', '2026-08-24', '2026-08-31',
            '2026-09-07', '2026-09-14', '2026-09-21', '2026-09-28', '2026-10-05', '2026-10-12'][i],
          freeHours: [0, 0, 14, 14, 36, 36, 50, 50, 50, 72, 72, 72, 72][i],
          freeFte: [0, 0, 0.4, 0.4, 0.9, 0.9, 1.3, 1.3, 1.3, 1.8, 1.8, 1.8, 1.8][i],
          belowCommitment: [0, 0, 0, 0, 1, 1, 1, 1, 1, 2, 2, 2, 2][i],
          headcount: 4 })) } };
      case 'team.rolloff': return { ...b, state: 'ok', data: {
        moneyVisible: true, windowEnd: '2027-01-20', asOf: '2026-07-20', revenueAtRisk90d: 7200,
        rows: [
          { userId: 31, name: 'Eva de Vries', client: 'Rabobank', clientCount: 1, endsOn: '2026-08-14', freeFrom: '2026-08-15', capacityReleased: 36, weeklyRevenue: 3960, priced: true },
          { userId: 58, name: 'Tom Jansen', client: 'ASML', clientCount: 1, endsOn: '2026-09-04', freeFrom: '2026-09-05', capacityReleased: 40, weeklyRevenue: 3240, priced: true }],
        weekly: Array.from({ length: 13 }, (_, i) => ({
          weekStart: ['2026-07-20', '2026-07-27', '2026-08-03', '2026-08-10', '2026-08-17', '2026-08-24', '2026-08-31',
            '2026-09-07', '2026-09-14', '2026-09-21', '2026-09-28', '2026-10-05', '2026-10-12'][i],
          comingFree: i === 3 ? 1 : (i === 7 ? 1 : 0),
          endingRevenueWeekly: i === 3 ? 3960 : (i === 7 ? 3240 : 0) })),
        monthly: [
          { monthStart: '2026-10-01', comingFree: 0, endingRevenueWeekly: 0 },
          { monthStart: '2026-11-01', comingFree: 0, endingRevenueWeekly: 0 },
          { monthStart: '2026-12-01', comingFree: 0, endingRevenueWeekly: 0 },
          { monthStart: '2027-01-01', comingFree: 0, endingRevenueWeekly: 0 }] } };
      case 'team.to_approve': return { ...b, state: 'ok', data: { count: 3, oldestWeekStart: '2026-07-06' } };
      case 'company.summary': return { ...b, state: 'ok', data: {
        memberCount: 46, companyUtilization: 79, target: 80, benchCount: 5, hoursTotal: 1584, approvalBacklogThisWeek: 9 } };
      case 'company.attention': return { ...b, state: 'ok', data: { rows: [
        { userId: 27, name: 'Ahmed Bakr', signal: 'overallocated', severity: 'high', detail: '112% this week', date: null },
        { userId: 31, name: 'Eva de Vries', signal: 'ending_soon', severity: 'med', detail: 'Rabobank assignment', date: '2026-07-25' },
        { userId: 42, name: 'Joost Meijer', signal: 'cert_expiring', severity: 'med', detail: 'AWS Solutions Architect', date: '2026-08-10' } ] } };
      case 'company.util_timeseries': return { ...b, state: 'ok', data: {
        target: 80,
        points: [
          { weekStart: '2026-04-13', utilization: 74 }, { weekStart: '2026-04-20', utilization: 76 },
          { weekStart: '2026-04-27', utilization: 81 }, { weekStart: '2026-05-04', utilization: 79 },
          { weekStart: '2026-05-11', utilization: 83 }, { weekStart: '2026-05-18', utilization: 77 },
          { weekStart: '2026-05-25', utilization: 80 }, { weekStart: '2026-06-01', utilization: 78 },
          { weekStart: '2026-06-08', utilization: 82 }, { weekStart: '2026-06-15', utilization: 75 },
          { weekStart: '2026-06-22', utilization: 79 }, { weekStart: '2026-06-29', utilization: 81 },
          { weekStart: '2026-07-06', utilization: 79 } ] } };
      case 'company.coming_free': return { ...b, state: 'ok', data: { rows: [
        { userId: 31, name: 'Eva de Vries', freeFrom: '2026-07-25', currentUtil: 92, lastProject: 'Rabobank data migration', matchCount: 3, bestMatchScore: 84 },
        { userId: 58, name: 'Tom Jansen', freeFrom: '2026-08-03', currentUtil: 68, lastProject: 'ASML BI dashboard', matchCount: 0, bestMatchScore: null } ],
        matchAsOf: '2026-07-27T02:15:00Z' } };
      case 'company.bench_forecast': return { ...b, state: 'ok', data: {
        horizonWeeks: 8, memberCount: 46,
        points: [
          { weekStart: '2026-07-13', booked: 41, bench: 5, benchHours: 152 },
          { weekStart: '2026-07-20', booked: 40, bench: 6, benchHours: 184 },
          { weekStart: '2026-07-27', booked: 38, bench: 8, benchHours: 248 },
          { weekStart: '2026-08-03', booked: 39, bench: 7, benchHours: 216 },
          { weekStart: '2026-08-10', booked: 37, bench: 9, benchHours: 280 },
          { weekStart: '2026-08-17', booked: 36, bench: 10, benchHours: 312 },
          { weekStart: '2026-08-24', booked: 38, bench: 8, benchHours: 248 },
          { weekStart: '2026-08-31', booked: 40, bench: 6, benchHours: 184 } ] } };
      case 'company.margin': return { ...b, state: 'ok', data: {
        marginPct: 34, revenue: 128400, cost: 84700, billableHrs: 1070, coveredHrs: 1058, coveragePct: 99, asOf: '2026-07-06' } };
      case 'company.bench': return { ...b, state: 'ok', data: {
        activeSeats: 46, placed: 41, benchCount: 5, rollOff30: 3, rollOff60: 6, rollOff90: 9 } };
      case 'company.margin_by_client': return { ...b, state: 'ok', data: {
        clients: [
          { kind: 'client', name: 'Rabobank', revenue: 61000, marginPct: 47, sharePct: 52, consultants: 7 },
          { kind: 'client', name: 'ING', revenue: 38000, marginPct: 41, sharePct: 32, consultants: 5 },
          { kind: 'other', name: null, revenue: null, marginPct: null, sharePct: null, consultants: 6 },
          { kind: 'unknown', name: null, revenue: null, marginPct: null, sharePct: null, consultants: 2 },
        ], topClientSharePct: 52, top3SharePct: 84, coveragePct: 92, asOf: '2026-06-26' } };
      case 'company.bench_roster': return { ...b, state: 'ok', data: {
        rows: [
          { userId: 11, name: 'Sanne de Vries', status: 'bench', since: '2026-06-01', until: null, client: null, matchCount: 5, bestMatchScore: 91 },
          { userId: 12, name: 'Ravi Patel', status: 'rolloff', since: null, until: '2026-07-20', client: 'ING', matchCount: 1, bestMatchScore: 62 },
        ], benchCount: 1, rollOff30: 1, rollOff60: 2, rollOff90: 4, matchAsOf: '2026-07-27T02:15:00Z' } };
    }
    throw new Error('Unhandled metric key: ' + q.key);
  }
}
